/**
 * Industry-standard website-field normalization, shared by every form that
 * collects a brand website (waitlist page, BrandWaitlistModal).
 *
 * Accepts what users actually paste — bare apex domains ("yourbrand.com"),
 * www hosts, and full http(s) URLs with paths/queries/trailing slashes —
 * and canonicalizes to the lowercase hostname. FE mirror of the BE's
 * `discovr_website` validator (internal/validate/engine.go): trim, prepend
 * https:// when no scheme, URL-parse, require a dotted host. The FE is
 * additionally stricter on scheme (http/https only). Never require a "www."
 * prefix — most real brand domains are apex.
 *
 * Returns:
 *   ''     — input empty/blank (field not provided; callers treat as optional)
 *   host   — normalized lowercase hostname (path/query dropped, the shape we
 *            have always sent to the BE)
 *   null   — invalid input (caller shows the validation error)
 */
export function normalizeWebsiteHost(raw: string): string | null {
  const s = raw.trim();
  if (!s) return '';
  if (/\s/.test(s)) return null;

  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(s);
  if (hasScheme && !/^https?:\/\//i.test(s)) return null;
  const candidate = hasScheme ? s : `https://${s}`;

  let host: string;
  try {
    host = new URL(candidate).hostname.toLowerCase();
  } catch {
    return null;
  }

  // A public website needs a dotted host with non-empty labels ("intranet",
  // ".com", "brand." are not shareable brand websites).
  if (!host.includes('.') || host.split('.').some((label) => label === '')) return null;
  return host;
}
